import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { notificationService } from '../services/api';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  LayoutDashboard,
  Clock,
  Calendar,
  Users,
  MapPin,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  ChevronDown,
  Shield,
  UserCog,
  BarChart3,
  ClipboardList,
  Banknote,
  ClipboardEdit,
  AlarmClock,
} from 'lucide-react';

const navItems = [
  {
    name: 'Dashboard',
    path: '/dashboard',
    icon: LayoutDashboard,
    roles: ['employee', 'manager', 'hr', 'admin', 'super_admin', 'store_manager']
  },
  {
    name: 'Attendance',
    path: '/dashboard/attendance',
    icon: Clock,
    roles: ['employee', 'manager', 'hr', 'admin', 'super_admin', 'store_manager']
  },
  {
    name: 'Leave Management',
    path: '/dashboard/leaves',
    icon: Calendar,
    roles: ['employee', 'manager', 'hr', 'admin', 'super_admin', 'store_manager']
  },
  {
    name: 'Corrections',
    path: '/dashboard/corrections',
    icon: ClipboardEdit,
    roles: ['employee', 'manager', 'hr', 'admin', 'super_admin']
  },
  {
    name: 'Team',
    path: '/dashboard/team',
    icon: Users,
    roles: ['manager', 'hr', 'admin', 'super_admin']
  },
  {
    name: 'Employees',
    path: '/dashboard/employees',
    icon: UserCog,
    roles: ['hr', 'admin', 'super_admin', 'store_manager']
  },
  {
    name: 'Shifts',
    path: '/dashboard/shifts',
    icon: AlarmClock,
    roles: ['hr', 'admin', 'super_admin']
  },
  {
    name: 'Locations',
    path: '/dashboard/locations',
    icon: MapPin,
    roles: ['hr', 'admin', 'super_admin']
  },
  {
    name: 'Reports',
    path: '/dashboard/reports',
    icon: FileText,
    roles: ['hr', 'admin', 'super_admin']
  },
  {
    name: 'Payroll',
    path: '/dashboard/payroll',
    icon: Banknote,
    roles: ['employee', 'manager', 'hr', 'admin', 'super_admin']
  },
  {
    name: 'Analytics',
    path: '/dashboard/analytics',
    icon: BarChart3,
    roles: ['hr', 'admin', 'super_admin']
  },
  {
    name: 'Audit Logs',
    path: '/dashboard/audit-logs',
    icon: ClipboardList,
    roles: ['admin', 'super_admin']
  },
  {
    name: 'User Management',
    path: '/dashboard/users',
    icon: Shield,
    roles: ['hr', 'admin', 'super_admin']
  },
];

export default function DashboardLayout() {
  const brandLogo = '/omlogo.png';
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifCount, setNotifCount] = useState(0);
  const [notifDetails, setNotifDetails] = useState({});
  const [notifOpen, setNotifOpen] = useState(false);

  useEffect(() => {
    const fetchNotifs = async () => {
      try {
        const data = await notificationService.getCount();
        setNotifCount(data.total || 0);
        setNotifDetails(data);
      } catch (_) {}
    };
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 60000); // refresh every minute
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const filteredNavItems = navItems.filter((item) =>
    item.roles.includes(user?.role || 'employee')
  );

  const getRoleBadgeColor = (role) => {
    const colors = {
      super_admin: 'bg-purple-100 text-purple-700 border-purple-200',
      admin: 'bg-red-100 text-red-700 border-red-200',
      hr: 'bg-blue-100 text-blue-700 border-blue-200',
      manager: 'bg-green-100 text-green-700 border-green-200',
      employee: 'bg-gray-100 text-gray-700 border-gray-200',
      store_manager: 'bg-teal-100 text-teal-700 border-teal-200',
    };
    return colors[role] || colors.employee;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#eef7f2] via-white to-[#f4efe6] flex">
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 om-sidebar transform transition-transform duration-200 ease-in-out lg:translate-x-0 lg:static ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b border-[#d7e7de]">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#033923] to-[#022b1b] flex items-center justify-center shadow-md shadow-[#033923]/15 p-2">
                <img src={brandLogo} alt="Organic Mandya" className="h-full w-full object-contain" />
              </div>
              <div>
                <h1 className="font-bold text-[#033923]">Organic Mandya</h1>
                <p className="text-xs text-[#5d746a] uppercase tracking-[0.2em]">HRMS Portal</p>
              </div>
            </div>
            <button
              className="lg:hidden p-1 text-[#33584b]"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <ScrollArea className="flex-1 py-4">
            <nav className="px-2 space-y-1">
              {filteredNavItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === '/dashboard'}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.name}</span>
                </NavLink>
              ))}
            </nav>
          </ScrollArea>

          <div className="p-4 border-t border-[#d7e7de] bg-[#f3f8f5]/90">
            <div className="flex items-center gap-3">
              <Avatar className="border-2 border-[#d7e7de]">
                <AvatarFallback className="bg-[#dcebe3] text-[#033923]">
                  {user?.username?.charAt(0).toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#033923] truncate">{user?.username}</p>
                <span className={`text-xs px-2 py-0.5 rounded-full border ${getRoleBadgeColor(user?.role)}`}>
                  {user?.role?.replace('_', ' ')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="flex-1 flex flex-col min-h-screen">
        <header className="sticky top-0 z-30 flex items-center justify-between h-16 px-4 om-header backdrop-blur-sm">
          <div className="flex items-center gap-4">
            <button
              className="lg:hidden p-2 hover:bg-[#edf4f0] rounded-lg text-[#033923]"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-toggle"
            >
              <Menu className="w-5 h-5" />
            </button>
            <h2 className="font-semibold text-[#033923] hidden sm:block">
              Welcome back, {user?.username}
            </h2>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                className="relative text-[#033923] hover:bg-[#edf4f0]"
                data-testid="notifications-btn"
                onClick={() => setNotifOpen(o => !o)}
              >
                <Bell className="w-5 h-5" />
                {notifCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center">
                    {notifCount > 9 ? '9+' : notifCount}
                  </span>
                )}
              </Button>
              {notifOpen && (
                <div className="notif-dropdown absolute right-0 top-10 w-72 max-w-[calc(100vw-1.5rem)] bg-white border border-[#d7e7de] rounded-xl shadow-lg z-50 p-3 space-y-2">
                  <p className="text-xs font-semibold text-[#5d746a] uppercase tracking-wide px-1">Notifications</p>
                  {notifCount === 0 ? (
                    <p className="text-sm text-muted-foreground px-1 py-2">All caught up!</p>
                  ) : (
                    <>
                      {notifDetails.pending_leaves > 0 && (
                        <button onClick={() => { navigate('/dashboard/leaves'); setNotifOpen(false); }}
                          className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#edf4f0] text-sm flex justify-between">
                          <span>Pending leave requests</span>
                          <span className="font-bold text-orange-500">{notifDetails.pending_leaves}</span>
                        </button>
                      )}
                      {notifDetails.pending_corrections > 0 && (
                        <button onClick={() => { navigate('/dashboard/corrections'); setNotifOpen(false); }}
                          className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#edf4f0] text-sm flex justify-between">
                          <span>Pending corrections</span>
                          <span className="font-bold text-orange-500">{notifDetails.pending_corrections}</span>
                        </button>
                      )}
                      {notifDetails.pending_payroll > 0 && (
                        <button onClick={() => { navigate('/dashboard/payroll'); setNotifOpen(false); }}
                          className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#edf4f0] text-sm flex justify-between">
                          <span>Payroll runs to finalize</span>
                          <span className="font-bold text-orange-500">{notifDetails.pending_payroll}</span>
                        </button>
                      )}
                      {notifDetails.missing_salary_structures > 0 && (
                        <button onClick={() => { navigate('/dashboard/payroll'); setNotifOpen(false); }}
                          className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#edf4f0] text-sm flex justify-between">
                          <span>Missing salary structures</span>
                          <span className="font-bold text-red-500">{notifDetails.missing_salary_structures}</span>
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 hover:bg-[#edf4f0]" data-testid="user-menu">
                  <Avatar className="w-8 h-8 border-2 border-[#d7e7de]">
                    <AvatarFallback className="bg-[#dcebe3] text-[#033923] text-sm">
                      {user?.username?.charAt(0).toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <ChevronDown className="w-4 h-4 text-[#5d746a]" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="text-[#033923]">{user?.username}</span>
                    <span className="text-xs text-[#5d746a]">{user?.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/dashboard/settings')} className="text-[#033923]">
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-3 sm:p-4 md:p-6 lg:p-8 min-w-0 overflow-x-hidden">
          <Outlet />
        </main>

        <footer className="border-t border-[#d7e7de] py-4 px-6 text-center text-sm text-[#5d746a]">
          Organic Mandya HRMS - Pure. Natural. Honest.
        </footer>
      </div>
    </div>
  );
}
